import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '../components/UI/Button';
import Card from '../components/UI/Card';
import { VILLA_NADIA_HERO_IMAGE, VILLA_NADIA_IMAGES } from '../data/villaNadiaImages';

const OurVillasScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const villas = [
    {
      id: 'najat',
      route: '/villa-najat',
      heroImage: '/assets/images/villa Baidane-15.jpg',
      detailImage: '/assets/images/villa Baidane-16.jpg',
      galleryImages: [
        '/assets/images/villa Baidane-15.jpg',
        '/assets/images/villa Baidane-17.jpg',
        '/assets/images/villa Baidane-18.jpg',
        '/assets/images/villa Baidane-19.jpg',
      ],
      badgeKey: 'ourVillas.najat.badge',
      badgeClass: 'bg-[#81736d]',
      titleKey: 'villas.najat.title',
      subtitleKey: 'villas.najat.subtitle',
      descriptionKey: 'villas.najat.description',
      pricingKey: 'villas.najat.pricing',
      stats: [
        { value: '450m²', labelKey: 'gallery.stats.livingSpace' },
        { value: '2', labelKey: 'villas.najat.bedrooms' },
        { value: '15x5m', labelKey: 'villas.najat.pool' },
        { value: '6', labelKey: 'villas.najat.capacity' },
      ],
      amenitiesKey: 'villas.najat.amenities',
      highlightsKey: 'villas.najat.highlights',
      bookable: true,
    },
    {
      id: 'nadia',
      route: '/villa-nadia',
      heroImage: VILLA_NADIA_HERO_IMAGE,
      detailImage: VILLA_NADIA_IMAGES[4],
      galleryImages: VILLA_NADIA_IMAGES.slice(0, 4),
      badgeKey: 'ourVillas.nadia.badge',
      badgeClass: 'bg-[#81736d]',
      titleKey: 'villas.nadia.title',
      subtitleKey: 'villas.nadia.subtitle',
      descriptionKey: 'villas.nadia.description',
      pricingKey: 'villas.nadia.pricing',
      stats: [
        { value: '450m²', labelKey: 'gallery.stats.livingSpace' },
        { value: '4', labelKey: 'villas.nadia.bedrooms' },
        { value: '15x5m', labelKey: 'villas.nadia.pool' },
        { value: '8', labelKey: 'villas.nadia.capacity' },
      ],
      amenitiesKey: 'villas.nadia.amenities',
      highlightsKey: 'villas.nadia.highlights',
      bookable: false,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section
        className="relative h-96 flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/assets/images/villa Baidane-33.jpg')`,
        }}
      >
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-sofia font-bold mb-4">
            {t('ourVillas.title')}
          </h1>
          <p className="text-xl font-sofia font-light mb-2">
            {t('ourVillas.subtitle')}
          </p>
          <p className="text-base font-sofia opacity-90 max-w-2xl mx-auto">
            {t('ourVillas.description')}
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg font-sofia text-text-secondary leading-relaxed">
            {t('ourVillas.intro')}
          </p>
        </div>
      </section>

      {/* Villa Cards */}
      {villas.map((villa, index) => {
        const amenities = t(villa.amenitiesKey, { returnObjects: true }) || [];
        const highlights = t(villa.highlightsKey, { returnObjects: true }) || [];
        const isReversed = index % 2 === 1;

        return (
          <section
            key={villa.id}
            className={`py-16 ${index % 2 === 0 ? 'bg-white' : ''}`}
            style={
              index % 2 === 1
                ? { background: 'url("/assets/icons/top-img-bg.png")' }
                : undefined
            }
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="mb-8">
                <span
                  className={`inline-block ${villa.badgeClass} text-white px-4 py-1 rounded-full text-sm font-sofia font-medium`}
                >
                  {t(villa.badgeKey)}
                </span>
              </div>

              <div
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start ${
                  isReversed ? 'lg:direction-rtl' : ''
                }`}
              >
                <div className={isReversed ? 'lg:order-2' : ''}>
                  <h2 className="text-3xl md:text-4xl font-sofia font-bold text-black mb-3">
                    {t(villa.titleKey)}
                  </h2>
                  <p className="text-lg font-sofia text-[#81736d] mb-4">
                    {t(villa.subtitleKey)}
                  </p>
                  <p className="text-md font-sofia text-[#81736d] mb-2">
                    {t(villa.pricingKey)}
                  </p>
                  <p className="text-lg font-sofia text-text-secondary mb-8">
                    {t(villa.descriptionKey)}
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-8">
                    {villa.stats.map((stat) => (
                      <div
                        key={stat.labelKey}
                        className="text-center p-4 bg-[#81736d] rounded-lg"
                      >
                        <div className="text-2xl font-sofia font-bold text-white">
                          {stat.value}
                        </div>
                        <div className="font-sofia text-black">{t(stat.labelKey)}</div>
                      </div>
                    ))}
                  </div>

                  {highlights.length > 0 && (
                    <ul className="text-sm font-sofia text-black mb-8 space-y-2">
                      {highlights.map((highlight, i) => (
                        <li key={i} className="flex items-center">
                          <span className="w-2 h-2 bg-[#81736d] rounded-full mr-3 flex-shrink-0"></span>
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button onClick={() => navigate(villa.route)}>
                      {t('ourVillas.viewDetails')}
                    </Button>
                    {villa.bookable ? (
                      <Button
                        variant="outline"
                        onClick={() => navigate('/booking')}
                      >
                        {t('villas.najat.bookNow')}
                      </Button>
                    ) : (
                      <Button variant="outline" onClick={() => navigate('/contact')}>
                        {t('common.contactUs')}
                      </Button>
                    )}
                  </div>
                </div>

                <div className={isReversed ? 'lg:order-1' : ''}>
                  <img
                    src={villa.detailImage}
                    alt={t(villa.titleKey)}
                    className="w-full h-80 object-cover rounded-lg shadow-lg mb-4"
                  />
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {villa.galleryImages.slice(0, 2).map((image, i) => (
                      <img
                        key={i}
                        src={image}
                        alt={`${t(villa.titleKey)} ${i + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Amenities preview */}
              <div className="mt-12">
                <h3 className="text-2xl font-sofia font-bold text-black mb-6">
                  {t('ourVillas.amenitiesPreview')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {amenities.slice(0, 9).map((amenity, i) => (
                    <div
                      key={i}
                      className="flex items-center bg-white p-3 rounded-lg shadow-sm"
                    >
                      <div className="w-5 h-5 bg-[#81736d] rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <span className="font-sofia text-text-primary text-sm">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Compare */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-sofia font-bold text-black text-center mb-4">
            {t('ourVillas.compareTitle')}
          </h2>
          <p className="text-center font-sofia text-text-secondary mb-10 max-w-2xl mx-auto">
            {t('ourVillas.compareDescription')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {villas.map((villa) => (
              <Card key={villa.id} className="overflow-hidden p-0">
                <img
                  src={villa.heroImage}
                  alt={t(villa.titleKey)}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-sofia font-bold text-[#81736d] mb-2">
                    {t(villa.titleKey)}
                  </h3>
                  <p className="font-sofia text-text-secondary text-sm mb-4">
                    {t(villa.descriptionKey)}
                  </p>
                  <Button className="w-full" onClick={() => navigate(villa.route)}>
                    {t('ourVillas.viewDetails')}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{
            backgroundImage: `url('${VILLA_NADIA_IMAGES[10]}')`,
          }}
        ></div>
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl font-sofia font-bold mb-4">
            {t('ourVillas.ctaTitle')}
          </h2>
          <p className="text-lg font-sofia font-light mb-8">
            {t('ourVillas.ctaDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-[#81736d] hover:bg-[#81736d]"
              onClick={() => navigate('/booking')}
            >
              {t('common.bookNow')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white hover:text-primary"
              onClick={() => navigate('/contact')}
            >
              {t('common.contactUs')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurVillasScreen;
